import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const sb = supabase as any;

// SM-2 inspired intervals: 1, 3, 7, 14, 30, 60 days
const INTERVALS = [1, 3, 7, 14, 30, 60];

function getNextInterval(currentInterval: number, wasCorrect: boolean, easeFactor: number): { interval: number; ease: number } {
  if (!wasCorrect) {
    // Reset to 1 day on incorrect, decrease ease
    return { interval: 1, ease: Math.max(1.3, easeFactor - 0.2) };
  }
  // Find next interval step
  const currentIdx = INTERVALS.findIndex(i => i >= currentInterval);
  const nextIdx = Math.min(currentIdx + 1, INTERVALS.length - 1);
  const newEase = Math.min(3.0, easeFactor + 0.1);
  return { interval: INTERVALS[nextIdx], ease: newEase };
}

export function useSpacedRepetition() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch count of items due for review
  const { data: dueCount = 0, isLoading: countLoading } = useQuery({
    queryKey: ["sr-due-count", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { count, error } = await sb
        .from("spaced_repetition_queue")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user!.id)
        .lte("next_review_at", new Date().toISOString());
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: 60000, // refresh every minute
  });

  // Fetch due MCQ IDs for review mode
  const { data: dueItems = [], isLoading: itemsLoading } = useQuery({
    queryKey: ["sr-due-items", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await sb
        .from("spaced_repetition_queue")
        .select("id, mcq_id, interval_days, ease_factor, review_count")
        .eq("user_id", user!.id)
        .lte("next_review_at", new Date().toISOString())
        .order("next_review_at", { ascending: true })
        .limit(50);
      if (error) throw error;
      return data as { id: string; mcq_id: string; interval_days: number; ease_factor: number; review_count: number }[];
    },
  });

  // Schedule a question for review (called on incorrect answer)
  const scheduleReview = useMutation({
    mutationFn: async (mcqId: string) => {
      if (!user?.id) throw new Error("Not authenticated");
      // Upsert: if already in queue, reset to 1-day interval
      const { error } = await sb
        .from("spaced_repetition_queue")
        .upsert(
          {
            user_id: user.id,
            mcq_id: mcqId,
            interval_days: 1,
            next_review_at: new Date(Date.now() + 86400000).toISOString(),
            review_count: 0,
            ease_factor: 2.5,
            last_reviewed_at: new Date().toISOString(),
          },
          { onConflict: "user_id,mcq_id" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sr-due-count"] });
      queryClient.invalidateQueries({ queryKey: ["sr-due-items"] });
    },
  });

  // Complete a review (update interval based on correct/incorrect)
  const completeReview = useMutation({
    mutationFn: async ({ mcqId, wasCorrect }: { mcqId: string; wasCorrect: boolean }) => {
      if (!user?.id) throw new Error("Not authenticated");
      
      // Find the current queue entry
      const item = dueItems.find(i => i.mcq_id === mcqId);
      const currentInterval = item?.interval_days ?? 1;
      const currentEase = item?.ease_factor ?? 2.5;
      const reviewCount = item?.review_count ?? 0;

      const { interval, ease } = getNextInterval(currentInterval, wasCorrect, currentEase);
      const nextReviewAt = new Date(Date.now() + interval * 86400000).toISOString();

      if (wasCorrect && interval >= 60) {
        // Mastered - remove from queue
        const { error } = await sb
          .from("spaced_repetition_queue")
          .delete()
          .eq("user_id", user.id)
          .eq("mcq_id", mcqId);
        if (error) throw error;
      } else {
        const { error } = await sb
          .from("spaced_repetition_queue")
          .upsert(
            {
              user_id: user.id,
              mcq_id: mcqId,
              interval_days: interval,
              next_review_at: nextReviewAt,
              review_count: reviewCount + 1,
              ease_factor: ease,
              last_reviewed_at: new Date().toISOString(),
            },
            { onConflict: "user_id,mcq_id" }
          );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sr-due-count"] });
      queryClient.invalidateQueries({ queryKey: ["sr-due-items"] });
    },
  });

  return {
    dueCount,
    dueItems,
    dueMcqIds: dueItems.map(i => i.mcq_id),
    isLoading: countLoading || itemsLoading,
    scheduleReview: scheduleReview.mutate,
    completeReview: completeReview.mutate,
  };
}
